import mapboxgl from 'mapbox-gl';
import {
  MAPBOX_STYLE,
  MELBOURNE_COORDINATES,
  MARKER_COLORS,
  POINTS_SOURCE,
  POINTS_CIRCLE_LAYER,
  POINTS_SYMBOL_LAYER,
  AUTO_PATH_SOURCE,
  AUTO_PATH_LAYER,
  CUSTOM_PATH_SOURCE,
  CUSTOM_PATH_LAYER,
  UNFINISHED_PATH_SOURCE,
  UNFINISHED_PATH_LAYER,
  DISTANCE_LABELS_SOURCE,
  DISTANCE_LABELS_LAYER,
  PATH_STYLES
} from './constants';
import { LocationType } from '../Sidebar/LocationList';
import { setupMarkerHoverEvents } from './MapMarkers';

/**
 * Initialize and setup Mapbox map with all necessary configurations
 * 
 * @param {Object} options - Options object
 * @param {Object} options.mapContainer - React ref for the map container element
 * @param {Function} options.setMapError - Function to set map error state
 * @param {Function} options.setMapReady - Function to set map ready state
 * @param {Object} options.sourcesReadyRef - React ref for tracking data source readiness
 * @param {boolean} options.shouldShowDistanceLabels - Flag to control distance labels visibility
 * @returns {Object|null} Map instance and utilities or null if initialization fails
 */
export const initializeMap = ({
  mapContainer,
  setMapError,
  setMapReady,
  sourcesReadyRef,
  shouldShowDistanceLabels
}) => {
  if (!mapContainer.current) return null;
  
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('mapbox_token') || process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
  if (!token) {
    setMapError('Missing Mapbox access token. Please add ?mapbox_token=your_token to URL or set REACT_APP_MAPBOX_ACCESS_TOKEN in .env');
    return null;
  }
  
  mapboxgl.accessToken = token;
  const map = new mapboxgl.Map({
    container: mapContainer.current,
    style: MAPBOX_STYLE,
    center: [MELBOURNE_COORDINATES.lng, MELBOURNE_COORDINATES.lat],
    zoom: 10,
    attributionControl: false,
  });
  
  map.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
  
  // Use a safely referenceable variable to track map instance
  let mapRef = map;
  
  // Listen for container size changes and resize map
  const resizeObserver = new ResizeObserver(() => {
    if (mapRef) {
      try {
        // Check if map container exists and is connected to DOM
        if (mapContainer.current && document.body.contains(mapContainer.current)) {
          mapRef.resize();
        }
      } catch (error) {
        console.error('Error during map resize:', error);
      }
    }
  });
  
  if (mapContainer.current) {
    resizeObserver.observe(mapContainer.current);
  }
  
  // When map loads, initialize data sources and layers
  map.on('load', () => {
    try {
      initializeMapLayers(map, shouldShowDistanceLabels);
      setupMarkerHoverEvents(map);
      
      sourcesReadyRef.current = true;
      setMapReady(true);
    } catch (err) {
      console.error("Error initializing map data sources and layers:", err);
      setMapError("Error initializing map data sources and layers");
    }
  });
  
  // Cleanup function to ensure map reference is cleared
  const cleanup = () => {
    mapRef = null;
  };
  
  return { map, resizeObserver, cleanup };
};

/**
 * Initialize all map layers and data sources
 * Creates sources and layers for:
 * - Auto path (solid line)
 * - Custom path (dashed line)
 * - Location points (circles with numbers)
 * - Distance labels
 * 
 * @param {Object} map - Mapbox map instance
 * @param {boolean} shouldShowDistanceLabels - Flag to control distance labels visibility
 */
export const initializeMapLayers = (map, shouldShowDistanceLabels) => {
  if (!map) return;
  
  // 1. Add auto path line data source
  map.addSource(AUTO_PATH_SOURCE, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  });
  
  // 2. Add auto path line layer
  map.addLayer({
    id: AUTO_PATH_LAYER,
    type: 'line',
    source: AUTO_PATH_SOURCE,
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
      'visibility': 'visible' // Initially set to visible, controlled by separate useEffect
    },
    paint: {
      // Use color specified in Feature, or default to auto path color
      'line-color': ['coalesce', ['get', 'color'], PATH_STYLES.auto.color],
      'line-width': PATH_STYLES.auto.width,
      'line-opacity': PATH_STYLES.auto.opacity,
      'line-dasharray': [1, 0] // Auto path uses solid line
    }
  });
  
  // 3. Add custom path line data source
  map.addSource(CUSTOM_PATH_SOURCE, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  });
  
  // 4. Add custom path line layer
  map.addLayer({
    id: CUSTOM_PATH_LAYER,
    type: 'line',
    source: CUSTOM_PATH_SOURCE,
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
      'visibility': 'visible' // Initially set to visible, controlled by separate useEffect
    },
    paint: {
      // Use color specified in Feature, or default to custom path color
      'line-color': ['coalesce', ['get', 'color'], PATH_STYLES.custom.color],
      'line-width': PATH_STYLES.custom.width,
      'line-opacity': PATH_STYLES.custom.opacity,
      'line-dasharray': [2, 2] // Custom path uses dashed line
    }
  });
  
  // 4.5 Add unfinished path line data source
  map.addSource(UNFINISHED_PATH_SOURCE, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  });
  
  // 4.6 Add unfinished path line layer
  map.addLayer({
    id: UNFINISHED_PATH_LAYER,
    type: 'line',
    source: UNFINISHED_PATH_SOURCE,
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
      'visibility': 'visible'
    },
    paint: {
      // Color based on path properties, use default color if not specified
      'line-color': ['coalesce', ['get', 'color'], PATH_STYLES.unfinished.color],
      
      // Line width based on path status: completed, unfinished but selected, unfinished and unselected
      'line-width': [
        'case',
        ['==', ['get', 'isCompleted'], true], 
        PATH_STYLES.custom.width, // Use custom path width for completed paths
        [
          'case',
          ['==', ['get', 'isSelected'], true], 
          PATH_STYLES.unfinished.width * 1.5, // Increase width by 50% for selected unfinished paths
          PATH_STYLES.unfinished.width // Use default width for unselected unfinished paths
        ]
      ],
      
      // Opacity based on path status
      'line-opacity': [
        'case',
        ['==', ['get', 'isCompleted'], true],
        PATH_STYLES.custom.opacity, // Use custom path opacity for completed paths
        [
          'case',
          ['==', ['get', 'isSelected'], true],
          PATH_STYLES.unfinished.opacity, // Use full opacity for selected unfinished paths
          PATH_STYLES.unfinished.opacity * 0.7 // Slightly more transparent for unselected unfinished paths
        ]
      ],
      
      // Line dash pattern based on path status
      'line-dasharray': [
        'case',
        ['==', ['get', 'isCompleted'], true],
        PATH_STYLES.custom.dasharray || [2, 2], // Use custom path dash pattern for completed paths
        PATH_STYLES.unfinished.dasharray // Use unfinished path dash pattern for unfinished paths
      ]
    }
  });
  
  // 5. Add points data source
  map.addSource(POINTS_SOURCE, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  });
  
  // 6. Add points circle layer
  map.addLayer({
    id: POINTS_CIRCLE_LAYER,
    type: 'circle',
    source: POINTS_SOURCE,
    paint: {
      // Circle color - set based on point type or if it's a start point
      'circle-color': [
        'case',
        ['==', ['get', 'isStart'], true], MARKER_COLORS[LocationType.START],
        ['==', ['get', 'type'], LocationType.END], MARKER_COLORS[LocationType.END],
        ['==', ['get', 'type'], LocationType.WAYPOINT], MARKER_COLORS[LocationType.WAYPOINT],
        MARKER_COLORS[LocationType.NORMAL]
      ],
      // Circle size - responsive based on zoom level
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8, ['case', ['==', ['get', 'isStart'], true], 4, 3],    // Very zoomed out: small dots
        10, ['case', ['==', ['get', 'isStart'], true], 6, 5],   // City level: medium dots
        12, ['case', ['==', ['get', 'isStart'], true], 18, 16], // District level: full circles with numbers
        15, ['case', ['==', ['get', 'isStart'], true], 20, 18], // Street level: larger circles
        18, ['case', ['==', ['get', 'isStart'], true], 22, 20]  // Building level: largest circles
      ],
      // Stroke width
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
      'circle-opacity': 0.95
    }
  });
  
  // 7. Add points symbol layer - numbers displayed directly on points
  map.addLayer({
    id: POINTS_SYMBOL_LAYER,
    type: 'symbol',
    source: POINTS_SOURCE,
    layout: {
      'text-field': ['get', 'name'],
      'text-font': ['Noto Sans Regular', 'Arial Unicode MS Regular'],
      // Text size - responsive and only visible at higher zoom levels
      'text-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8, 0,   // Zoom 8: no text (invisible)
        10, 0,  // Zoom 10: no text (invisible)
        12, ['case', ['==', ['get', 'isStart'], true], 16, 14], // Zoom 12+: show numbers
        15, ['case', ['==', ['get', 'isStart'], true], 18, 16], // Zoom 15+: larger text
        18, ['case', ['==', ['get', 'isStart'], true], 20, 18]  // Zoom 18+: largest text
      ],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'text-anchor': 'center',
      'text-offset': [0, 0],
      'text-justify': 'center'
    },
    paint: {
      'text-color': '#ffffff',
      // Text opacity - fully hidden at low zoom levels
      'text-opacity': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8, 0,    // Zoom 8: completely hidden
        10, 0,   // Zoom 10: completely hidden
        11, 0,   // Zoom 11: completely hidden
        12, 1.0, // Zoom 12+: fully visible
        18, 1.0  // Zoom 18: fully visible
      ],
      // Remove text halo for cleaner look inside circles
      'text-halo-width': 0
    }
  });
  
  // 8. Add distance labels data source
  map.addSource(DISTANCE_LABELS_SOURCE, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  });
  
  // 9. Add distance labels layer
  map.addLayer({
    id: DISTANCE_LABELS_LAYER,
    type: 'symbol',
    source: DISTANCE_LABELS_SOURCE,
    layout: {
      'text-field': ['get', 'distance'],
      'text-font': ['Noto Sans Regular', 'Arial Unicode MS Regular'],
      // Responsive text size based on zoom level
      'text-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        10, 0,  // Zoom 10: no text (invisible)
        11, 12, // Zoom 11: small text (district level)
        12, 14, // Zoom 12: normal text
        15, 16, // Zoom 15: larger text
        18, 18  // Zoom 18: largest text
      ],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'symbol-placement': 'point',
      'text-justify': 'center',
      'text-anchor': 'center',
      'visibility': shouldShowDistanceLabels ? 'visible' : 'none'
    },
    paint: {
      'text-color': '#000000',
      'text-halo-width': 3,
      'text-halo-color': 'rgba(255,255,255,0.95)',
      // Responsive opacity - auto show at district level
      'text-opacity': [
        'interpolate',
        ['linear'],
        ['zoom'],
        8, 0,   // Zoom 8: completely hidden
        9, 0,   // Zoom 9: completely hidden
        10, 0,  // Zoom 10: completely hidden (city level)
        11, 0.8, // Zoom 11: show at district level
        12, 0.9, // Zoom 12+: fully visible
        18, 0.9  // Zoom 18: fully visible
      ]
    }
  });
}; 